import React, { useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import ConfirmModal from '../common/ConfirmModal';
import api from '../../services/api';

function DistributionHistory({ sessions, onRefresh, loading }) {
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  // Selective rollback state
  const [showSelectiveRollbackModal, setShowSelectiveRollbackModal] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectiveRollingBack, setSelectiveRollingBack] = useState(false);

  // Swipe gesture state
  const [swipedId, setSwipedId] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 100; // minimum swipe distance in pixels

  // Full rollback handlers
  const handleRollbackClick = (session) => {
    if (session.status === 'rolled_back') {
      toast.info('This distribution has already been rolled back');
      return;
    }
    setSelectedSession(session);
    setShowRollbackModal(true);
  };

  const handleConfirmRollback = async () => {
    if (!selectedSession) return;

    try {
      setRollingBack(true);
      await api.post(`/distribution/rollback/${selectedSession.id}`);
      toast.success(`Successfully rolled back distribution of ${selectedSession.leadCount} leads`);
      setShowRollbackModal(false);
      setSelectedSession(null);
      setSwipedId(null); // Reset swipe state
      onRefresh(); // Refresh history data
    } catch (error) {
      console.error('Rollback failed:', error);
      toast.error(error.response?.data?.message || 'Failed to rollback distribution');
    } finally {
      setRollingBack(false);
    }
  };

  // Selective rollback handlers
  const handleRecipientClick = (session, recipientId, recipientData) => {
    if (session.status === 'rolled_back') {
      toast.info('This distribution has already been rolled back');
      return;
    }

    // Check if this specific recipient has been rolled back
    if (session.rolledBackRecipients?.includes(recipientId)) {
      toast.info('This recipient has already been rolled back');
      return;
    }

    setSelectedSession(session);
    setSelectedRecipient({ id: recipientId, ...recipientData });
    setShowSelectiveRollbackModal(true);
  };

  const handleConfirmSelectiveRollback = async () => {
    if (!selectedSession || !selectedRecipient) return;

    try {
      setSelectiveRollingBack(true);
      const response = await api.post(
        `/distribution/rollback/${selectedSession.id}/${selectedRecipient.id}`
      );
      toast.success(
        `Successfully rolled back ${response.data.revertedCount} leads from ${selectedRecipient.name}`
      );
      setShowSelectiveRollbackModal(false);
      setSelectedSession(null);
      setSelectedRecipient(null);
      onRefresh(); // Refresh history data
    } catch (error) {
      console.error('Selective rollback failed:', error);
      toast.error(error.response?.data?.message || 'Failed to rollback distribution for recipient');
    } finally {
      setSelectiveRollingBack(false);
    }
  };

  // Swipe gesture handlers
  const handleTouchStart = (e, sessionId) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e, sessionId) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (session) => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && session.status !== 'rolled_back') {
      // Swipe left to reveal rollback button
      setSwipedId(session.id);
    } else if (isRightSwipe) {
      // Swipe right to hide rollback button
      setSwipedId(null);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getDistributionTypeLabel = (type) => {
    return type === 'manager_to_leaders' ? 'Manager → Leaders' : 'Leader → Agents';
  };

  const getPoolTypeColor = (poolType) => {
    switch (poolType) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'old':
        return 'bg-purple-100 text-purple-800';
      case 'recycled':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLeadTypeColor = (leadType) => {
    switch (leadType) {
      case 'personal':
        return 'bg-green-100 text-green-800';
      case 'team':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return '';
    }
  };

  if (sessions.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        No distribution history found
      </div>
    );
  }

  return (
    <>
      {/* Full Rollback Confirmation Modal */}
      <ConfirmModal
        isOpen={showRollbackModal && selectedSession}
        onClose={() => {
          setShowRollbackModal(false);
          setSelectedSession(null);
        }}
        onConfirm={handleConfirmRollback}
        title="Confirm Rollback"
        message={
          selectedSession ? (
            <>
              <p className="text-gray-700 mb-4 mt-0">
                Are you sure you want to rollback this distribution?
              </p>

              <div className="bg-yellow-50 border border-yellow-400 rounded-md p-4 mb-4">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-600 text-xl">⚠️</span>
                  <div className="flex-1">
                    <p className="font-semibold text-yellow-800 m-0 mb-1">Warning</p>
                    <p className="text-sm text-yellow-700 m-0">
                      This will revert <strong>{selectedSession.leadCount} leads</strong> with status "new" back to the previous pool.
                      Leads that have been worked on (beyond "new" status) will remain with their current agent.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="text-xs text-gray-600 space-y-1">
                  <div><strong>Pool:</strong> {selectedSession.poolType.toUpperCase()}</div>
                  <div><strong>Type:</strong> {getDistributionTypeLabel(selectedSession.distributionType)}</div>
                  <div><strong>Distributed:</strong> {formatDate(selectedSession.createdAt)}</div>
                  <div><strong>Recipients:</strong> {selectedSession.recipientCount}</div>
                </div>
              </div>
            </>
          ) : null
        }
        confirmText="Rollback Distribution"
        confirmVariant="danger"
        loading={rollingBack}
      />

      {/* Selective Rollback Confirmation Modal */}
      <ConfirmModal
        isOpen={showSelectiveRollbackModal && selectedSession && selectedRecipient}
        onClose={() => {
          setShowSelectiveRollbackModal(false);
          setSelectedSession(null);
          setSelectedRecipient(null);
        }}
        onConfirm={handleConfirmSelectiveRollback}
        title="Confirm Selective Rollback"
        message={
          selectedSession && selectedRecipient ? (
            <>
              <p className="text-gray-700 mb-4 mt-0">
                Are you sure you want to rollback leads distributed to <strong>{selectedRecipient.name}</strong>?
              </p>

              <div className="bg-blue-50 border border-blue-400 rounded-md p-4 mb-4">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 text-xl">ℹ️</span>
                  <div className="flex-1">
                    <p className="font-semibold text-blue-800 m-0 mb-1">Selective Rollback</p>
                    <p className="text-sm text-blue-700 m-0">
                      This will only rollback <strong>{selectedRecipient.count} leads</strong> with status "new" that were distributed to this specific recipient.
                      Leads that have progressed beyond "new" status will remain with the recipient.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="text-xs text-gray-600 space-y-1">
                  <div><strong>Recipient:</strong> {selectedRecipient.name}</div>
                  <div><strong>Leads:</strong> {selectedRecipient.count}</div>
                  <div><strong>Pool:</strong> {selectedSession.poolType.toUpperCase()}</div>
                  <div><strong>Type:</strong> {getDistributionTypeLabel(selectedSession.distributionType)}</div>
                  <div><strong>Distributed:</strong> {formatDate(selectedSession.createdAt)}</div>
                </div>
              </div>
            </>
          ) : null
        }
        confirmText="Rollback for Recipient"
        confirmVariant="danger"
        loading={selectiveRollingBack}
      />

      {/* History List */}
      <div className="bg-white rounded-lg p-3 md:p-4 lg:p-5 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <div>
            <h3 className="m-0 mb-1 text-lg text-gray-800">Distribution History</h3>
            <p className="m-0 text-gray-500 text-xs">
              View and rollback past distributions
            </p>
          </div>
        </div>

        {sessions.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            No distribution history found
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {sessions.map((session) => {
                // Check if this is a partially rolled-back session
                const hasPartialRollback = session.rolledBackRecipients && session.rolledBackRecipients.length > 0 && session.status !== 'rolled_back';
                const isFullyRolledBack = session.status === 'rolled_back';

                return (
                  <div
                    key={session.id}
                    className={`relative overflow-hidden border rounded-lg transition-all ${
                      isFullyRolledBack
                        ? 'bg-gray-50 border-gray-300 opacity-70'
                        : 'bg-white border-gray-200 hover:border-tenq-gold hover:shadow-md'
                    }`}
                    onTouchStart={(e) => handleTouchStart(e, session.id)}
                    onTouchMove={(e) => handleTouchMove(e, session.id)}
                    onTouchEnd={() => handleTouchEnd(session)}
                  >
                    {/* Swipe-revealed rollback button (mobile only) */}
                    {swipedId === session.id && !isFullyRolledBack && (
                      <div className="md:hidden absolute inset-y-0 right-0 flex items-center bg-red-500 px-4">
                        <button
                          className="text-white font-semibold text-sm"
                          onClick={() => handleRollbackClick(session)}
                        >
                          Rollback
                        </button>
                      </div>
                    )}

                    <div
                      className={`p-4 transition-transform duration-200 ${
                        swipedId === session.id ? '-translate-x-20' : 'translate-x-0'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row justify-between items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getPoolTypeColor(session.poolType)}`}>
                              {session.poolType.toUpperCase()}
                            </span>
                            {session.leadType && (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getLeadTypeColor(session.leadType)}`}>
                                {session.leadType.toUpperCase()}
                              </span>
                            )}
                            <span className="text-sm font-medium text-gray-700">
                              {getDistributionTypeLabel(session.distributionType)}
                            </span>
                            {isFullyRolledBack && (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">
                                FULLY ROLLED BACK
                              </span>
                            )}
                            {hasPartialRollback && (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-800">
                                PARTIALLY ROLLED BACK ({session.rolledBackRecipients.length})
                              </span>
                            )}
                          </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          <div>
                            <strong>{session.leadCount} leads</strong> distributed to{' '}
                            <strong>{session.recipientCount} {session.distributionType === 'manager_to_leaders' ? 'leaders' : 'agents'}</strong>
                          </div>
                          <div className="text-xs">
                            Performed by <strong>{session.performedBy?.name || 'Unknown'}</strong> on{' '}
                            {formatDate(session.createdAt)}
                          </div>
                          {session.status === 'rolled_back' && session.rolledBackAt && (
                            <div className="text-xs text-red-600">
                              Rolled back on {formatDate(session.rolledBackAt)}
                            </div>
                          )}
                        </div>

                        {/* Distribution Breakdown */}
                        {session.distributionResults && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs font-semibold text-gray-700 mb-2">
                              Distribution Breakdown:
                              {session.status !== 'rolled_back' && (
                                <span className="ml-2 text-tenq-gold font-normal">(Click recipient to rollback)</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {Object.entries(session.distributionResults).map(([recipientId, data]) => {
                                const isRecipientRolledBack = session.rolledBackRecipients?.includes(recipientId);
                                const isSessionRolledBack = session.status === 'rolled_back';
                                const canRollback = !isSessionRolledBack && !isRecipientRolledBack;

                                return (
                                  <div
                                    key={recipientId}
                                    className={`rounded px-2 py-1 text-xs relative ${
                                      canRollback
                                        ? 'bg-gray-50 hover:bg-tenq-gold/10 hover:border-tenq-gold border border-transparent cursor-pointer transition-all'
                                        : isRecipientRolledBack
                                        ? 'bg-red-50 border border-red-200 opacity-60'
                                        : 'bg-gray-50'
                                    }`}
                                    onClick={() => {
                                      if (canRollback) {
                                        handleRecipientClick(session, recipientId, data);
                                      }
                                    }}
                                  >
                                    <div className="font-medium text-gray-800">{data.name}</div>
                                    <div className="text-gray-600">{data.count} leads</div>
                                    {isRecipientRolledBack && (
                                      <div className="absolute -top-1 -right-1">
                                        <span className="bg-red-500 text-white text-[10px] px-1 py-0.5 rounded-full font-semibold">
                                          ROLLED BACK
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Desktop Rollback Button */}
                      {!isFullyRolledBack && (
                        <button
                          type="button"
                          onClick={() => handleRollbackClick(session)}
                          className="hidden md:block px-4 py-2 bg-red-500 text-white rounded-md text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
                          disabled={loading}
                        >
                          Rollback Remaining
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DistributionHistory;
